import React, { useState, useEffect, useCallback, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { Spinner, NonIdealState, Button, Icon, Switch } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import Mindmap from './Mindmap'; // The core mindmap component
import { useAppStore } from '../../stores/UnifiedStoreProvider';
import api from '../../services/api';

interface CollaborativeMindmapProps {
  mindmapId: string;
  onNodeDoubleClick?: (node: any) => void;
  onNodeExpand?: (nodeId: string) => void;
  selectedNodeId?: string | null;
}

const CollaborativeMindmap: React.FC<CollaborativeMindmapProps> = observer(({ 
  mindmapId,
  onNodeDoubleClick,
  onNodeExpand,
  selectedNodeId
}) => {
  const appStore = useAppStore();
  const { mindmap, workspaceRealtime } = appStore;
  const { isLoading, nodes, reactFlowNodes, reactFlowEdges } = mindmap;
  const [dataLoading, setDataLoading] = useState(true);
  const [autoLayout, setAutoLayout] = useState(true);
  const [showControls, setShowControls] = useState(false);

  // Track presence: notify server which mindmap we're viewing
  useEffect(() => {
    if (mindmapId && workspaceRealtime.isConnected) {
      console.log(`👁️  [CollaborativeMindmap] Setting current mindmap for presence: ${mindmapId}`);
      workspaceRealtime.setCurrentMindmap(mindmapId);
    }
    
    return () => {
      // Clear when unmounting or switching mindmaps
      if (workspaceRealtime.isConnected) {
        console.log(`👋 [CollaborativeMindmap] Clearing current mindmap from presence`);
        workspaceRealtime.setCurrentMindmap(null);
      }
    };
  }, [mindmapId, workspaceRealtime]);

  useEffect(() => {
    const loadMindmap = async () => {
      try {
        setDataLoading(true);
        console.log(`🔍 [CollaborativeMindmap] Loading mindmap: ${mindmapId}`);
        console.log(`📊 [CollaborativeMindmap] Current state:`, {
          isLoading,
          dataLoading: true,
          nodesCount: nodes.length,
          currentMindmapId: mindmap.mindmapId
        });
        
        // Auth is handled via httpOnly cookies
    
        if (!mindmapId) {
          console.error('❌ [CollaborativeMindmap] No mindmap ID provided');
          setDataLoading(false);
          return;
        }
        
        // Load the specific mindmap
        console.log(`📡 [CollaborativeMindmap] Fetching mindmap data from API: /mindmaps/${mindmapId}`);
        const mindmapResponse = await api.get(`/mindmaps/${mindmapId}`, {
          withCredentials: true
        });

        console.log('✅ [CollaborativeMindmap] Mindmap data received:', {
          id: mindmapResponse.data.id,
          name: mindmapResponse.data.name,
          nodesCount: mindmapResponse.data.nodes?.length || 0,
          edgesCount: mindmapResponse.data.edges?.length || 0
        });
        
        // Initialize the store with the fetched data
        mindmap.setMindmapId(mindmapResponse.data.id);
        console.log(`💾 [CollaborativeMindmap] Set mindmap ID in store: ${mindmapResponse.data.id}`);
        
        if (mindmapResponse.data.nodes && mindmapResponse.data.nodes.length > 0) {
          console.log(`🎨 [CollaborativeMindmap] Initializing mindmap with ${mindmapResponse.data.nodes.length} nodes`);
          mindmap.initializeData({
            nodes: mindmapResponse.data.nodes,
            edges: mindmapResponse.data.edges || [],
            version: 0
          });
      } else {
          console.log('📭 [CollaborativeMindmap] No nodes in mindmap, using empty state');
          mindmap.initializeData({ nodes: [], edges: [], version: 0 });
        }
        
        console.log(`🏁 [CollaborativeMindmap] Load complete. Store state:`, {
          mindmapId: mindmap.mindmapId,
          nodesCount: mindmap.nodes?.length || 'undefined',
          reactFlowEdgesCount: mindmap.reactFlowEdges?.length || 'undefined',
          hasNodes: !!mindmap.nodes
        });
        
        // Load GitHub links for this mindmap
        await mindmap.loadGithubLinks();
        
        // Load comments for all nodes
        const nodeIds = mindmap.nodes.map((n: any) => n.id);
        await Promise.all(nodeIds.map((nodeId: string) => mindmap.loadNodeComments(nodeId)));
        
        console.log(`✅ [CollaborativeMindmap] Loaded metadata:`, {
          githubLinks: mindmap.githubLinks.length,
          comments: mindmap.comments.length
        });
      } catch (error) {
        console.error('❌ [CollaborativeMindmap] Error loading mindmap:', error);
        mindmap.setError('Failed to load mindmap');
      } finally {
        setDataLoading(false);
        console.log(`⏹️ [CollaborativeMindmap] dataLoading set to false`);
      }
    };

    console.log(`🔄 [CollaborativeMindmap] useEffect triggered for mindmapId: ${mindmapId}`);
    loadMindmap();
  }, [mindmapId, mindmap]);

  const showEmptyState = !isLoading && !dataLoading && nodes.length === 0;
  
  console.log(`🎭 [CollaborativeMindmap] Render decision:`, {
    showEmptyState,
    isLoading,
    dataLoading,
    nodesLength: nodes.length,
    reactFlowNodesLength: reactFlowNodes.length
  });
  
  // Define callback BEFORE any conditional returns to maintain hooks order
  const handleAddRootNode = useCallback(async () => {
    try {
      // Auth is handled via httpOnly cookies
      
      const newNode = {
        type: 'root',
        label: 'New Root Node',
        position: { x: 250, y: 250 },
        parentId: null,
        isRoot: true,
        priority: 'medium'
      };
      
      // Call backend API to create node
      const response = await api.post(
        `/mindmaps/${mindmap.mindmapId}/nodes`,
        newNode,
        
      );
      
      // Reload mindmap to get updated data
      const mindmapResponse = await api.get(
        `/mindmaps/${mindmap.mindmapId}`,
        
      );
      
      mindmap.initializeData({
        nodes: mindmapResponse.data.nodes || [],
        edges: mindmapResponse.data.edges || [],
        version: 0
              });
          } catch (error) {
      console.error('Error adding root node:', error);
    }
  }, [mindmap]);
  
  // Render loading state AFTER all hooks are defined
  // Temporarily disabled loading animation
  /*
  if (isLoading || dataLoading) {
    return (
      <div className="mindmap-loading-container" style={{
        width: '100%',
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Spinner size={Spinner.SIZE_LARGE} />
        <p>Loading Mindmap...</p>
      </div>
    );
  }
  */

  return (
    <div className="collaborative-mindmap-container" style={{ width: '100%', height: '100%', position: 'relative' }}>
      {showEmptyState ? (
        <NonIdealState
          icon="folder-open"
          title="Empty Mindmap"
          description="Create a root node to get started."
          action={
            <Button
              icon={IconNames.ADD}
              intent="primary"
              text="Add Root Node"
              onClick={handleAddRootNode}
            />
          }
        />
      ) : (
        <div style={{ width: '100%', height: '100%' }}>
          <Mindmap
            key={mindmap.mindmapId}
            nodes={reactFlowNodes}
            edges={reactFlowEdges}
            autoLayout={autoLayout}
            onNodeDoubleClick={onNodeDoubleClick}
            onNodeExpand={onNodeExpand}
            selectedNodeId={selectedNodeId}
            // onNodesChange and onEdgesChange will be handled by the store internally
          />
        </div>
      )}
    </div>
  );
});

export default CollaborativeMindmap;
